/**
 * «Перегнать в хозяйство» — смена закрепления техники (AK-747).
 *
 * Развилка founder 25.08 решена так: техника закреплена ЗА ХОЗЯЙСТВОМ, а
 * перегон — смена закрепления, не новая запись. Поэтому К-744, уехавший в мае
 * пахать к соседу, пропадает из карточки Аргамака и появляется в карточке
 * Зимовников — с той же историей работ и тем же инвентарным номером.
 *
 * Выбор хозяйства — выпадашка «алфавит + поиск» (канон). Это ФОРМА, а не
 * линза шапки: `/scope/set` здесь не при чём, человек не меняет то, что видит,
 * он говорит, куда уехала машина.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Stack } from '@/components/ui/stack';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import { cn } from '@/lib/utils';
import { machineryPlural } from './machineryLogic';

export type FarmOption = { id: string; name: string };

/**
 * Список хозяйств выпадашки: алфавит + поиск, текущее хозяйство исключено.
 *
 * Исключаем намеренно: «перегнать в то же самое» — не действие, а ловушка,
 * человек нажмёт и решит, что система его не поняла.
 */
export function reassignTargets(
  farms: readonly FarmOption[],
  currentFarmId: string,
  search: string,
): FarmOption[] {
  return farms
    .filter((f) => f.id !== currentFarmId && matchesSearch(f.name, search))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

export function ReassignMachineryDialog({
  open,
  count,
  farms,
  currentFarmId,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  count: number;
  farms: readonly FarmOption[];
  currentFarmId: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (farmId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPicked(null);
    setError(null);
  }, [open]);

  const options = useMemo(
    () => reassignTargets(farms, currentFarmId, search),
    [farms, currentFarmId, search],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Перегнать технику</DialogTitle>
          <DialogDescription>
            {machineryPlural(count)} сменит закрепление и пропадёт из этой
            карточки — история работ и номера останутся при машине.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={3}>
          <div>
            <Label htmlFor="reassign-farm">Куда перегоняем *</Label>
            <Input
              id="reassign-farm"
              className="mt-1"
              value={search}
              placeholder="Поиск по названию хозяйства"
              aria-invalid={error ? true : undefined}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <ScrollArea className="h-44 rounded-md border border-border">
            {options.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">
                Других хозяйств, куда можно перегнать, не видно.
              </p>
            ) : (
              <ul>
                {options.map((farm) => (
                  <li key={farm.id}>
                    <button
                      type="button"
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm hover:bg-muted',
                        picked === farm.id && 'bg-muted font-semibold',
                      )}
                      onClick={() => {
                        setPicked(farm.id);
                        setError(null);
                      }}
                    >
                      {farm.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </Stack>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button
            disabled={pending}
            data-testid="machinery-reassign-confirm"
            onClick={() => {
              if (!picked) {
                setError('Выберите хозяйство, куда уехала техника');
                return;
              }
              onConfirm(picked);
            }}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Перегнать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
